import { validateFormField } from '../../common/helpers/validateFormField';
import { FormFieldTypes } from '../../constants/FormFieldTypes';
import { Fuel as VehicleFuels } from '../../constants/Vehicle';
import { REQUIRED_PHOTOS_AMOUNT } from '../../constants/RequiredPhotosAmount';
import { ServiceHistory } from '../../constants/ServiceHistory';
import { PhotoPerspective } from '../../constants/PhotoPerspective';
import { AuctionStatusType } from '../../constants/AuctionStatusType';

export const validateVehicleRegistrationPlate = (values) => {
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { registrationPlate } = values?.basicData;
  const errors = {
    'Vehicle Registration': validateFormField(registrationPlate, null, true, 10),
  };
  return removeEmptyEntries(errors);
};

export const validateVehicleBasicData = (values) => {
  const {
    registrationPlate,
    mileage,
    makeSelect,
    modelSelect,
    year,
    derivative,
    carType,
    transmission,
    engineSize,
    fuel,
    colour,
    doors,
    dateOfFirstRegistration,
    previousKeepersInTotal,
    vin,
    v5cLogBook,
    ulezCompliant,
    vehicleType,
    vatQualifying,
    // eslint-disable-next-line no-unsafe-optional-chaining
  } = values?.basicData;

  const fuelsWithoutEngine = [VehicleFuels.ELECTRIC];
  const isEngineRequired = !fuelsWithoutEngine.includes(values?.basicData?.fuel?.value);

  const errors = {
    'Vehicle Registration': validateFormField(registrationPlate, null, true, 10),
    Millage: validateFormField(mileage, FormFieldTypes.NUMBER, true, 9),
    Make: validateFormField(makeSelect?.value, FormFieldTypes.STRING, true),
    Model: validateFormField(modelSelect?.value, FormFieldTypes.STRING, true),
    Year: validateFormField(year, FormFieldTypes.PROD_YEAR, true, 4), // year is a string, not object
    Derivative: validateFormField(derivative, FormFieldTypes.DERIVATIVE, true, 150),
    'Body Type': validateFormField(carType?.value, FormFieldTypes.STRING, true, 20),
    Transmission: validateFormField(transmission?.value, FormFieldTypes.STRING, true, 40),
    'Engine Size': validateFormField(
      engineSize,
      FormFieldTypes.ENGINE_SIZE,
      isEngineRequired,
      9,
    ),
    'Vehicle Type': validateFormField(vehicleType?.value, FormFieldTypes.STRING, true),
    'VAT Qualifying': validateFormField(vatQualifying?.label, FormFieldTypes.STRING, true),
    Fuel: validateFormField(fuel?.value, FormFieldTypes.STRING, true, 20),
    Colour: validateFormField(colour?.value, FormFieldTypes.STRING, true), // colour is an object with .value
    Doors: validateFormField(doors, FormFieldTypes.DOORS, true, 2), // doors is a string/number, not object
    'Registration Date': validateFormField(
      dateOfFirstRegistration,
      FormFieldTypes.DATE_OF_REG,
      true,
      10, // dd/mm/yyyy is 10 chars
    ),
    'Previous Keepers': validateFormField(
      previousKeepersInTotal,
      FormFieldTypes.PREV_KEEPERS,
      true,
      2,
    ),
    VIN: validateFormField(vin, FormFieldTypes.VIN, true, 17),
    'V5C logbook present': validateFormField(v5cLogBook, null, true),
    'ULEZ compliant': validateFormField(ulezCompliant, null, true),
  };
  return removeEmptyEntries(errors);
};

export const validateVehicleTyres = (values) => {
  const errors = {
    'Nearside Front': validateFormField(
      values?.tyreThreadDepths?.nearsideFront,
      FormFieldTypes.STRING,
      true,
    ),
    'Nearside Rear': validateFormField(
      values?.tyreThreadDepths?.nearsideRear,
      FormFieldTypes.STRING,
      true,
    ),
    'Offside Front': validateFormField(
      values?.tyreThreadDepths?.offsideFront,
      FormFieldTypes.STRING,
      true,
    ),
    'Offside Rear': validateFormField(
      values?.tyreThreadDepths?.offsideRear,
      FormFieldTypes.STRING,
      true,
    ),
  };
  return removeEmptyEntries(errors);
};

export const validateVehiclePhotos = (values) => {
  const photos = values?.vehicleMedia?.photos || [];
  const enoughPhotos =
    photos.filter((photo) => !!photo.fileUrl).length >= REQUIRED_PHOTOS_AMOUNT;
  if (!enoughPhotos) {
    return 'Not enough photos';
  }

  const requiredPerspectives = Object.values(PhotoPerspective).filter(
    (perspective) => perspective !== PhotoPerspective.OTHER,
  );

  const hasAllRequiredPerspectives = requiredPerspectives.every((required) =>
    photos.some((photo) => photo.perspective === required && !!photo.fileUrl),
  );

  if (!hasAllRequiredPerspectives) {
    return 'Not enough perspectives';
  }

  return {};
};

export const validateVehicleDescription = (values) => {
  const errors = {
    Description: !values?.descriptionAndSettings?.description?.length
      ? 'Field is required'
      : null,
  };
  return removeEmptyEntries(errors);
};

export const validateVehicleFeatures = (values) => {
  let enoughFeatures;
  if (values?.capFeatures?.length) {
    enoughFeatures =
      !!values?.capFeatures?.filter((it) => !!it)?.length ||
      !!values?.vehicleFeatures?.vehicleFeaturesCustom.length;
  } else {
    enoughFeatures =
      !!values?.vehicleFeatures?.vehicleFeaturesDictionary?.length ||
      !!values?.vehicleFeatures?.vehicleFeaturesCustom?.length;
  }
  return enoughFeatures ? {} : 'Not enough features';
};

export const validateServiceHistory = (values) => {
  if (values.serviceHistory?.type) {
    if (
      typeof values?.serviceHistory?.type === 'object'
        ? values?.serviceHistory?.type?.value === ServiceHistory.NONE
        : values?.serviceHistory?.type === ServiceHistory.NONE
    ) {
      return {};
    }
    if (values?.photoRecords?.length) {
      return {};
    }
    if (
      !values?.manualRecords?.filter((it) => !!it.date || !!it.mileage || !!it.serviceName)
        ?.length
    ) {
      return { serviceHistoryForm: 'Manual records or scans should be specified' };
    }

    let errors = {};
    values.manualRecords.forEach((it) => {
      const dateError = !it?.date || !it?.date?.length ? 'Field is required' : null;
      const mileageError =
        !it?.mileage || !it?.mileage?.toString().length ? 'Field is required' : null;
      const serviceNameError =
        !it?.serviceName || !it?.serviceName?.length ? 'Field is required' : null;
      if (!!dateError?.length && !Object.keys(errors).includes('Date')) {
        errors = {
          ...errors,
          Date: dateError,
        };
      }
      if (!!mileageError?.length && !Object.keys(errors).includes('Mileage')) {
        errors = {
          ...errors,
          Mileage: mileageError,
        };
      }
      if (!!serviceNameError?.length && !Object.keys(errors).includes('Service name')) {
        errors = {
          ...errors,
          'Service name': serviceNameError,
        };
      }
    });
    return errors;
  }
  return { serviceHistoryForm: 'Service History Type not specified' };
};

export const validateLaunchData = (values) => {
  let errors = {
    Location: !values?.vehicleLocation?.value?.length ? 'Field is required' : null,
    // Time (auctionEndDate) is always required, auctionStartDate can be null if auction starts immediately
    Time: !values?.auctionEndDate?.length ? 'Field is required' : null,
    Status: !values?.type?.value?.length ? 'Field is required' : null,
  };

  // Reserve Price is required only if standardAuctionAndBuyNow checkbox is checked
  if (values?.standardAuctionAndBuyNow) {
    errors = {
      ...errors,
      'Reserve Price':
        !values?.autoAcceptPriceInPence || !values?.autoAcceptPriceInPence?.toString().length
          ? 'Field is required'
          : null,
    };
  }

  // Handle other auction types
  if (values?.type?.value === AuctionStatusType.MINIMUM_PRICE) {
    errors = {
      ...errors,
      'No Reserve Price':
        !values?.minimumPriceInPence || !values?.minimumPriceInPence?.toString().length
          ? 'Field is required'
          : null,
    };
  }
  return removeEmptyEntries(errors);
};

const removeEmptyEntries = (obj) =>
  Object.keys(obj).reduce((result, key) => {
    if (obj[key] != null) {
      result[key] = obj[key];
    }
    return result;
  }, {});
